import { useT, itemName, rarityLabel } from '@/i18n';
import { getItem } from '@/domain/items';
import { upgradedStats } from '@/domain/upgrade';
import { useGameStore, ascensionCostV2 } from '@/state/gameStore';
import { canAfford, maxLevel } from '@/domain/upgrade';
import type { EquipmentInstance, EquipmentStats, GlitchPerk, Rarity } from '@/domain/types';
import { SET_NAMES } from '@/domain/itemSets';
import { countItem } from '@/domain/inventory';
import { ItemIcon } from './ItemIcon';
import './ItemDetailModal.css';

// P2.C Reforge cost — kept in one place so a future balance pass can tune
// without hunting the value across multiple files.
const REFORGE_NANO_CHIP_COST = 5;
const REFORGE_DATA_TRACK_COST = 1;

function glitchPerkLabel(perk: GlitchPerk): string {
  switch (perk) {
    case 'vampiric':   return '🩸 Vampiric';
    case 'overloaded': return '💥 Overloaded';
    case 'hollow':     return '⊘ Hollow';
  }
}

interface ItemDetailModalProps {
  instance: EquipmentInstance;
  onClose: () => void;
  onUpgrade: (inst: EquipmentInstance) => void;
  readOnly?: boolean;
}

const RARITY_COLOR: Record<Rarity, string> = {
  common: '#a4b8c8', uncommon: '#4affb0', rare: '#4aa7ff',
  epic: '#c64aff', legendary: '#ff9a2b', mythic: '#ff4af0',
};

function statsFor(inst: EquipmentInstance | undefined): EquipmentStats {
  if (!inst) return { attack: 0, defense: 0, health: 0 };
  const def = getItem(inst.itemId);
  if (!def?.stats) return { attack: 0, defense: 0, health: 0 };
  const base = inst.rolledStats ?? def.stats;
  return upgradedStats(base, inst.upgradeLevel, def.stats);
}

interface BonusSummary {
  attack: number;     // flat ATK from rolled bonus stats
  defense: number;    // flat DEF
  health: number;     // flat HP
  loot_chance: number; // multiplier fraction (0.05 = +5%)
  xp_gain: number;
}

function bonusSumsFor(inst: EquipmentInstance | undefined): BonusSummary {
  const out: BonusSummary = { attack: 0, defense: 0, health: 0, loot_chance: 0, xp_gain: 0 };
  if (!inst?.bonusStats) return out;
  for (const b of inst.bonusStats) {
    if (b.isMultiplier) {
      if (b.stat === 'loot_chance' || b.stat === 'xp_gain') out[b.stat] += b.value;
    } else if (b.stat === 'attack' || b.stat === 'defense' || b.stat === 'health') {
      out[b.stat] += b.value;
    }
  }
  return out;
}

function bonusStatLabel(stat: string, value: number, isMultiplier: boolean): string {
  if (isMultiplier) {
    const pct = Math.round(value * 100);
    const label = stat === 'loot_chance' ? 'Loot' : stat === 'xp_gain' ? 'XP' : stat;
    return `+${pct}% ${label}`;
  }
  const label = stat === 'attack' ? 'ATK' : stat === 'defense' ? 'DEF' : stat === 'health' ? 'HP' : stat;
  return `+${value} ${label}`;
}

function diffClass(d: number): string {
  if (d > 0) return 'item-diff--up';
  if (d < 0) return 'item-diff--down';
  return 'item-diff--same';
}

function diffSign(d: number): string {
  if (d > 0) return `+${d}`;
  if (d < 0) return `${d}`;
  return '±0';
}

export function ItemDetailModal({ instance, onClose, onUpgrade, readOnly = false }: ItemDetailModalProps) {
  const player = useGameStore((s) => s.player);
  const equipInstance = useGameStore((s) => s.equipInstance);
  const unequipSlot = useGameStore((s) => s.unequipSlot);
  const salvageInstance = useGameStore((s) => s.salvageInstance);
  const ascendInstance = useGameStore((s) => s.ascendInstance);
  const reforgeBonusStats = useGameStore((s) => s.reforgeBonusStats);
  const t = useT();

  const def = getItem(instance.itemId);
  if (!def || !player) return null;

  const isEquipped = Object.values(player.equipment).some((eq) => eq?.id === instance.id);

  // v3 ascension: 3 universal Ascension-Fragments instead of 3 exact duplicates.
  // Fragments are earned by salvaging any L10 item, so progression isn't gated
  // on rolling 4 copies of the same drop.
  const currentTier = instance.ascensionTier ?? (instance.ascended ? 1 : 0);
  const fragments = player.ascensionFragments ?? 0;
  const v2Cost = currentTier < 3 ? ascensionCostV2((currentTier + 1) as 1 | 2 | 3) : null;
  const canAscend = currentTier < 3
    && instance.upgradeLevel >= maxLevel(def)
    && fragments >= 3;
  const ascendAffordable = canAscend && v2Cost
    && (player.cleanCode ?? 0) >= v2Cost.cleanCode
    && canAfford(player, { data_track: v2Cost.data_track });
  const ascendCostLines: Array<{ label: string; ok: boolean }> = v2Cost ? [
    { label: `${v2Cost.cleanCode.toLocaleString('de-DE')} ◈`, ok: (player.cleanCode ?? 0) >= v2Cost.cleanCode },
    { label: `${v2Cost.data_track} Data-Track`, ok: canAfford(player, { data_track: v2Cost.data_track }) },
    { label: `3 Fragments (du hast: ${fragments})`, ok: fragments >= 3 },
  ] : [];
  const slot = def.equipSlot;
  const currentEquipped = slot ? player.equipment[slot] : undefined;
  const currentDef = currentEquipped ? getItem(currentEquipped.itemId) : null;

  const newStats = statsFor(instance);
  const currentStats = statsFor(currentEquipped);
  const diff = {
    attack: newStats.attack - currentStats.attack,
    defense: newStats.defense - currentStats.defense,
    health: newStats.health - currentStats.health,
  };
  const newBonus = bonusSumsFor(instance);
  const curBonus = bonusSumsFor(currentEquipped);
  const bonusDiff = {
    attack: newBonus.attack - curBonus.attack,
    defense: newBonus.defense - curBonus.defense,
    health: newBonus.health - curBonus.health,
    loot_chance: newBonus.loot_chance - curBonus.loot_chance,
    xp_gain: newBonus.xp_gain - curBonus.xp_gain,
  };
  const hasAnyBonusChange =
    bonusDiff.attack !== 0 || bonusDiff.defense !== 0 || bonusDiff.health !== 0 ||
    Math.abs(bonusDiff.loot_chance) > 0.0001 || Math.abs(bonusDiff.xp_gain) > 0.0001;

  const color = RARITY_COLOR[def.rarity];

  const handleEquip = () => {
    equipInstance(instance.id);
    onClose();
  };

  const handleSalvage = () => {
    if (confirm(t.inventory.salvageConfirm)) {
      salvageInstance(instance.id);
      onClose();
    }
  };

  const handleUpgrade = () => {
    onUpgrade(instance);
  };

  const handleUnequip = () => {
    if (slot) {
      unequipSlot(slot);
      onClose();
    }
  };

  return (
    <div className="item-detail__overlay" onClick={onClose}>
      <div className="item-detail" onClick={(e) => e.stopPropagation()}>
        <div className="item-detail__header" style={{ borderColor: color }}>
          <ItemIcon itemId={def.id} sizeRem={3.2} />
          <div className="item-detail__heading">
            <div className="item-detail__name">
              {itemName(def.nameKey)}
              <span className="item-detail__level" style={{ color }}>+{instance.upgradeLevel}</span>
            </div>
            <div className="item-detail__rarity" style={{ color }}>
              {rarityLabel(def.rarity)} · {slot ? t.character.slots[slot] : '—'}
              {def.weaponClass && def.weaponClass !== 'standard' && (
                <span className="item-detail__badge item-detail__badge--weapon">
                  {def.weaponClass === 'heavy' ? '🔨 Schwer' : '⚡ Schnell'}
                </span>
              )}
            </div>
            {def.setId && (
              <div className="item-detail__set-chip">
                ◈ {SET_NAMES[def.setId]}
              </div>
            )}
            {def.aspectAffinity && (
              <div
                className="item-detail__affinity-chip"
                title="Trägt zur Aspekt-Affinität bei — 3/6/8 Teile = ×1.5/×1.75/×2 Passive-Multiplikator"
              >
                ✦ {def.aspectAffinity.toUpperCase()} Affinity
              </div>
            )}
            {currentTier > 0 && (
              <div className={`item-detail__ascended-banner item-detail__ascended-banner--t${currentTier}`}>
                {'✦'.repeat(currentTier)} {currentTier >= 3 ? 'Mythic' : `Ascended T${currentTier}`}
                {instance.glitchPerks && instance.glitchPerks.length > 0 && (
                  <span className="item-detail__glitch-perks">
                    · {instance.glitchPerks.map((p) => glitchPerkLabel(p)).join(' · ')}
                  </span>
                )}
              </div>
            )}
            {def.legendaryEffect && (
              <div className="item-detail__legendary-banner">
                {(t as unknown as Record<string, Record<string, string>>).legendaryEffects?.[def.legendaryEffect] ?? def.legendaryEffect}
              </div>
            )}
            <div className="item-detail__value">◈ {def.baseValue}</div>
          </div>
        </div>

        {/* Absolute stats */}
        <div className="item-detail__stats">
          <div className="item-detail__stats-title">
            Stats
            {instance.rolledStats && (
              <span className="item-detail__rolled-badge">gewürfelt</span>
            )}
          </div>
          {def.stats && def.stats.attack > 0 && (
            <div className="stat-row">
              <span>⚔ {t.character.attack}</span>
              <span className="stat-row__val">{newStats.attack}</span>
            </div>
          )}
          {def.stats && def.stats.defense > 0 && (
            <div className="stat-row">
              <span>🛡 {t.character.defense}</span>
              <span className="stat-row__val">{newStats.defense}</span>
            </div>
          )}
          {def.stats && def.stats.health > 0 && (
            <div className="stat-row">
              <span>♥ {t.character.hp}</span>
              <span className="stat-row__val">{newStats.health}</span>
            </div>
          )}
        </div>

        {/* Bonus stats */}
        {instance.bonusStats && instance.bonusStats.length > 0 && (
          <div className="item-detail__bonus-stats">
            <div className="item-detail__stats-title">✦ Bonus</div>
            {instance.bonusStats.map((b, i) => (
              <div key={i} className={`stat-row stat-row--bonus ${b.isMultiplier ? 'stat-row--mult' : ''}`}>
                <span>{bonusStatLabel(b.stat, b.value, b.isMultiplier)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Comparison with currently equipped */}
        {!readOnly && !isEquipped && slot && (
          <div className="item-detail__compare">
            <div className="item-detail__compare-title">
              {currentEquipped && currentDef
                ? `Vergleich zu: ${itemName(currentDef.nameKey)} +${currentEquipped.upgradeLevel}`
                : `Slot aktuell leer — Differenz beim Anlegen:`}
            </div>
            <div className={`compare-row ${diffClass(diff.attack)}`}>
              <span>⚔ ATK</span>
              <span className="compare-row__vals">
                {currentStats.attack} <span className="arrow">→</span> {newStats.attack}
              </span>
              <span className="compare-row__delta">{diffSign(diff.attack)}</span>
            </div>
            <div className={`compare-row ${diffClass(diff.defense)}`}>
              <span>🛡 DEF</span>
              <span className="compare-row__vals">
                {currentStats.defense} <span className="arrow">→</span> {newStats.defense}
              </span>
              <span className="compare-row__delta">{diffSign(diff.defense)}</span>
            </div>
            <div className={`compare-row ${diffClass(diff.health)}`}>
              <span>♥ HP</span>
              <span className="compare-row__vals">
                {currentStats.health} <span className="arrow">→</span> {newStats.health}
              </span>
              <span className="compare-row__delta">{diffSign(diff.health)}</span>
            </div>
            {hasAnyBonusChange && (
              <>
                <div className="item-detail__compare-subtitle">✦ Bonus-Stats</div>
                {bonusDiff.attack !== 0 && (
                  <div className={`compare-row ${diffClass(bonusDiff.attack)}`}>
                    <span>⚔ ATK</span>
                    <span className="compare-row__vals">
                      +{curBonus.attack} <span className="arrow">→</span> +{newBonus.attack}
                    </span>
                    <span className="compare-row__delta">{diffSign(bonusDiff.attack)}</span>
                  </div>
                )}
                {bonusDiff.defense !== 0 && (
                  <div className={`compare-row ${diffClass(bonusDiff.defense)}`}>
                    <span>🛡 DEF</span>
                    <span className="compare-row__vals">
                      +{curBonus.defense} <span className="arrow">→</span> +{newBonus.defense}
                    </span>
                    <span className="compare-row__delta">{diffSign(bonusDiff.defense)}</span>
                  </div>
                )}
                {bonusDiff.health !== 0 && (
                  <div className={`compare-row ${diffClass(bonusDiff.health)}`}>
                    <span>♥ HP</span>
                    <span className="compare-row__vals">
                      +{curBonus.health} <span className="arrow">→</span> +{newBonus.health}
                    </span>
                    <span className="compare-row__delta">{diffSign(bonusDiff.health)}</span>
                  </div>
                )}
                {Math.abs(bonusDiff.loot_chance) > 0.0001 && (
                  <div className={`compare-row ${diffClass(bonusDiff.loot_chance)}`}>
                    <span>✦ Loot</span>
                    <span className="compare-row__vals">
                      +{Math.round(curBonus.loot_chance * 100)}% <span className="arrow">→</span> +{Math.round(newBonus.loot_chance * 100)}%
                    </span>
                    <span className="compare-row__delta">{diffSign(Math.round(bonusDiff.loot_chance * 100))}%</span>
                  </div>
                )}
                {Math.abs(bonusDiff.xp_gain) > 0.0001 && (
                  <div className={`compare-row ${diffClass(bonusDiff.xp_gain)}`}>
                    <span>✦ XP</span>
                    <span className="compare-row__vals">
                      +{Math.round(curBonus.xp_gain * 100)}% <span className="arrow">→</span> +{Math.round(newBonus.xp_gain * 100)}%
                    </span>
                    <span className="compare-row__delta">{diffSign(Math.round(bonusDiff.xp_gain * 100))}%</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* P2.C Reforge section — re-roll bonus-stat affixes for a small CC
            cost. Only shown for items that can actually carry affixes
            (uncommon+), since common items always roll empty bonus-stats. */}
        {!readOnly && def.rarity !== 'common' && (() => {
          const haveChips = countItem(player.inventory, 'nano_chip');
          const haveTracks = countItem(player.inventory, 'data_track');
          const canAffordReforge = haveChips >= REFORGE_NANO_CHIP_COST
            && haveTracks >= REFORGE_DATA_TRACK_COST;
          const handleReforge = () => {
            if (!confirm('Bonus-Stats neu auswürfeln? Die aktuellen Werte werden überschrieben.')) return;
            reforgeBonusStats(instance.id);
            // Modal stays open — the new stats render on the next frame as
            // the player state flushes.
          };
          return (
            <div className="item-detail__reforge">
              <div className="item-detail__reforge-title">⟲ Reforge — Bonus-Stats neu auswürfeln</div>
              <div className="item-detail__reforge-info">
                Neue Affixe gemäß der Item-Seltenheit ({rarityLabel(def.rarity)}) — die alten gehen verloren.
              </div>
              <div className="item-detail__reforge-cost">
                <span className={haveChips >= REFORGE_NANO_CHIP_COST ? '' : 'item-detail__reforge-cost-line--fail'}>
                  {REFORGE_NANO_CHIP_COST}× Nano-Chip (du hast: {haveChips})
                </span>
                <span className={haveTracks >= REFORGE_DATA_TRACK_COST ? '' : 'item-detail__reforge-cost-line--fail'}>
                  {REFORGE_DATA_TRACK_COST}× Data-Track (du hast: {haveTracks})
                </span>
              </div>
              <button
                className="btn btn--reforge"
                disabled={!canAffordReforge}
                onClick={handleReforge}
              >
                {canAffordReforge ? '⟲ Reforge' : 'Ressourcen fehlen'}
              </button>
            </div>
          );
        })()}

        {/* Ascend section — v2: tier-based, requires 3 stash duplicates */}
        {!readOnly && currentTier < 3 && instance.upgradeLevel >= maxLevel(def) && (
          <div className="item-detail__ascend">
            <div className="item-detail__ascend-title">
              ✦ Ascension T{currentTier} → T{currentTier + 1}
              {currentTier + 1 === 3 && <span> · Mythic</span>}
            </div>
            <div className="item-detail__ascend-info">
              Stats × {(Math.pow(1.5, currentTier + 1)).toFixed(2)} · neuer Glitch-Perk · Lv-Reset
            </div>
            <div className="item-detail__ascend-cost">
              {ascendCostLines.map((c, i) => (
                <span
                  key={i}
                  className={`item-detail__ascend-cost-line ${c.ok ? '' : 'item-detail__ascend-cost-line--fail'}`}
                >
                  {c.label}
                </span>
              ))}
            </div>
            <button
              className="btn btn--ascend"
              disabled={!ascendAffordable}
              onClick={() => { ascendInstance(instance.id); onClose(); }}
            >
              {ascendAffordable
                ? `✦ Ascend zu T${currentTier + 1}`
                : !canAscend ? `${fragments}/3 Fragments` : 'Kosten zu hoch'}
            </button>
          </div>
        )}

        <div className="item-detail__actions">
          {!readOnly && !isEquipped && (
            <button className="btn btn--primary" onClick={handleEquip}>
              {t.character.equip}
            </button>
          )}
          {/* Core 3 slots (weapon/helmet/armor) are swap-only — once equipped
              they always must hold an item, since unequipping leaves the
              character visually broken (e.g. no helmet → headless body, no
              weapon → bare-handed punches). The remaining 5 slots are
              optional and can be cleared by the player. */}
          {!readOnly && isEquipped && slot && !['weapon', 'helmet', 'armor'].includes(slot) && (
            <button className="btn btn--ghost" onClick={handleUnequip}>
              ✕ Ablegen
            </button>
          )}
          {!readOnly && def.stats && (
            <button className="btn btn--ghost" onClick={handleUpgrade}>
              ↑ {t.character.upgrade}
            </button>
          )}
          {!readOnly && !isEquipped && (
            <button className="btn btn--danger" onClick={handleSalvage}>
              🔧 {t.inventory.salvage}
            </button>
          )}
          <button className="btn btn--ghost" onClick={onClose}>
            {t.upgrade.cancel}
          </button>
        </div>
      </div>
    </div>
  );
}
